import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useKeycloak } from '@react-keycloak/web';
import { AgentFile } from '@/features/agents/hooks/use-export-agent';

interface ImportAgentData {
  agentFile: AgentFile;
  dryRun?: boolean;
}

export interface ImportAgentResponse {
  mode: 'create' | 'overwrite';
  uuid: string;
  title: string;
  existingTitle?: string | null;
  files?: string[];
  revision?: number;
}

export function useImportAgent() {
  const { keycloak } = useKeycloak();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      importData: ImportAgentData
    ): Promise<ImportAgentResponse> => {
      const response = await fetch(
        `${
          import.meta.env.VITE_IMPORT_ENGINE_ENDPOINT
        }/api/v1/bundles/generate_bundle/functions/import_agent`,
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${keycloak.token}`,
          },
          method: 'POST',
          body: JSON.stringify(importData),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || 'The agent could not be uploaded.');
      }

      return data.data;
    },
    onSuccess: (_data, variables) => {
      if (variables.dryRun) {
        return;
      }

      queryClient.invalidateQueries({ queryKey: ['userAgents'] });
      queryClient.invalidateQueries({ queryKey: ['agent'] });
    },
  });
}
